import { Link } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import CategoryQuickLinks from "@/pages/home/components/CategoryQuickLinks";
import EditorPicks from "@/pages/home/components/EditorPicks";
import SpecialPreview from "@/pages/home/components/SpecialPreview";
import SectionHeading from "@/components/feature/SectionHeading";
import FilmCard from "@/components/feature/FilmCard";
import { allFilms, allSpecials, getLatestFilms } from "@/lib/catalog";
import { usePageMeta } from "@/hooks/usePageMeta";

export default function Home() {
  usePageMeta(
    "日韩伦理片 - 精选日韩伦理影视作品专区",
    "日韩伦理片网站为您提供丰富的日韩伦理影视内容介绍、剧情解析及作品推荐，涵盖多类型经典与热门伦理影片，满足用户深入了解日韩伦理影视需求。",
  );

  const latest = getLatestFilms(8);
  const analysisCount = allFilms.reduce((total, film) => total + film.analysis.length, 0);

  return (
    <div>
      <HeroCarousel />
      <CategoryQuickLinks />

      <section className="mx-auto max-w-shell px-4 pt-12 md:px-8 md:pt-16">
        <SectionHeading
          kicker="Latest Arrivals"
          title="最新上线"
          description="按收录时间排列的新条目，包含院线长片、限定剧与影展短片。每条都附有剧情简介与编辑解析。"
          actionLabel="查看全部分类"
          actionTo="/category/%E7%94%B5%E5%BD%B1"
        />
        <div className="grid grid-cols-2 gap-x-4 gap-y-8 md:grid-cols-3 md:gap-x-5 lg:grid-cols-4">
          {latest.map((film) => (
            <FilmCard key={film.id} film={film} />
          ))}
        </div>
      </section>

      <section className="mt-14 border-y border-background-200 bg-background-100/70 py-12 md:mt-20 md:py-16">
        <div className="mx-auto max-w-shell px-4 md:px-8">
          <SectionHeading
            kicker="Editor's Picks"
            title="编辑推荐"
            description="由编辑部反复重看后选出的作品，兼顾叙事完成度与议题深度。适合作为进入日韩伦理题材的起点。"
            actionLabel="查看更多专题"
            actionTo="/specials"
          />
          <EditorPicks />
        </div>
      </section>

      <section className="bg-primary-800">
        <div className="mx-auto grid max-w-shell grid-cols-2 gap-y-8 px-4 py-10 md:grid-cols-4 md:px-8 md:py-12">
          {[
            { value: String(allFilms.length), label: "收录作品", sub: "Films indexed" },
            { value: String(allSpecials.length), label: "策划专题", sub: "Curated specials" },
            { value: String(analysisCount), label: "解析段落", sub: "Analysis notes" },
            { value: "3", label: "内容分类", sub: "Films / Series / Shorts" },
          ].map((stat) => (
            <div key={stat.label} className="flex flex-col">
              <span className="font-heading text-[32px] font-extrabold leading-none text-accent-300 md:text-[40px]">
                {stat.value}
              </span>
              <span className="mt-2 text-[13px] font-medium text-background-50">{stat.label}</span>
              <span className="mt-0.5 font-label text-[10px] uppercase tracking-[0.16em] text-primary-200/70">
                {stat.sub}
              </span>
            </div>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-shell px-4 py-14 md:px-8 md:py-16">
        <SectionHeading
          kicker="Curated Specials"
          title="推荐专题"
          description="按主题而非年份整理的片单。每个专题都写明了入选理由，方便你顺着一条线索连续观看。"
          actionLabel="全部专题"
          actionTo="/specials"
        />
        <SpecialPreview />
      </section>

      <section className="border-t border-background-200 bg-background-100/60">
        <div className="mx-auto grid max-w-shell grid-cols-1 gap-8 px-4 py-12 md:px-8 md:py-14 lg:grid-cols-[0.9fr_1.4fr] lg:gap-16">
          <div>
            <p className="flex items-center gap-2 font-label text-[11px] font-semibold uppercase tracking-[0.22em] text-accent-700">
              <span className="h-[2px] w-5 bg-accent-500" />
              About This Archive
            </p>
            <h2 className="mt-2.5 font-heading text-[24px] font-extrabold leading-tight tracking-tight text-foreground-950 md:text-[30px]">
              我们如何整理
              <br />
              一个敏感的题材
            </h2>
          </div>
          <div>
            <p className="text-[13px] leading-[1.9] text-foreground-700 md:text-sm">
              伦理题材最容易滑向猎奇。这个资料库的做法是：只讨论作品，不消费画面。每个条目都由编辑统一撰写简介与解析，标注导演、主演、年份、地区与时长，并尽量把作品放回它的社会语境里。
            </p>
            <p className="mt-3 text-[13px] leading-[1.9] text-foreground-600 md:text-sm">
              站内收藏与分享均为浏览器本地演示，不收集任何个人信息。如果你发现条目信息有误，欢迎通过反馈表单告诉我们。
            </p>
            <div className="mt-5 flex flex-wrap gap-2.5">
              <Link
                to="/about"
                className="flex cursor-pointer items-center gap-1.5 rounded-md bg-primary-800 px-4 py-2.5 text-[12px] font-medium text-background-50 transition-colors hover:bg-primary-700"
              >
                了解网站定位
                <i className="ri-arrow-right-line text-xs" />
              </Link>
              <Link
                to="/search"
                className="flex cursor-pointer items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-[12px] font-medium text-foreground-800 transition-colors hover:border-primary-300 hover:text-primary-700"
              >
                先随便搜一部
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}